import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/lib/auth';
import { PageTitle } from '@/components/layout/PageTitle';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

interface AccountView {
    id: string;
    status: string;
    tenant_handle: string;
    environment: string;
    isolation_level: string;
    created: string;
    organization_name: string;
    application_key: string;
    application_name?: string;
    subdomain?: string;
}

export default function AccountsListPage() {
    const { fetchWithAuth } = useAuth();
    const [accounts, setAccounts] = useState<AccountView[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchWithAuth('/api/accounts')
            .then((res) => res.json())
            .then((data) => setAccounts(data.data ?? []))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [fetchWithAuth]);

    return (
        <div className="space-y-6">
            <PageTitle
                title="Accounts"
                subtitle="Customer workspaces across your applications"
            />

            {loading ? (
                <div className="text-muted-foreground text-sm">Loading accounts...</div>
            ) : accounts.length === 0 ? (
                <div className="text-muted-foreground text-sm">
                    No accounts found. Accounts are created when customers enroll via white-label partner applications.
                </div>
            ) : (
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Handle</TableHead>
                                <TableHead>Application</TableHead>
                                <TableHead>Environment</TableHead>
                                <TableHead>Organization</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Created</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {accounts.map((account) => (
                                <TableRow key={account.id}>
                                    <TableCell>
                                        <Link
                                            to={`/accounts/${account.id}`}
                                            className="font-medium text-primary hover:underline"
                                        >
                                            {account.tenant_handle}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{account.application_name ?? account.application_key}</TableCell>
                                    <TableCell>
                                        <Badge variant="outline">{account.environment}</Badge>
                                    </TableCell>
                                    <TableCell>{account.organization_name}</TableCell>
                                    <TableCell>
                                        <Badge variant={account.status === 'Active' ? 'default' : 'secondary'}>
                                            {account.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground text-sm">
                                        {new Date(account.created).toLocaleDateString()}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
}
